import React, { useState, useRef, useEffect } from "react";
import { Play, X, ChevronLeft, ChevronRight } from "lucide-react";

const listingSamples = [
  {
    id: 0,
    videoId: "1213412294",
    num: "01",
    label: "Cinematic Hype Edit",
    thumb: "https://vumbnail.com/1213412294.jpg",
  },
  {
    id: 1,
    videoId: "1200144003",
    num: "02",
    label: "Walking & Talking Tour",
    thumb: "https://vumbnail.com/1200144003.jpg",
  },
  {
    id: 2,
    videoId: "1213685706",
    num: "03",
    label: "Sped-Up Walkthrough",
    thumb: "https://vumbnail.com/1213685706.jpg",
  },
  {
    id: 3,
    videoId: "1200146201",
    num: "04",
    label: "B-Roll Highlight · Vol. 1",
    thumb: "https://vumbnail.com/1200146201.jpg",
  },
  {
    id: 4,
    videoId: "1200213203",
    num: "05",
    label: "B-Roll Highlight · Vol. 2",
    thumb: "https://vumbnail.com/1200213203.jpg",
  },
];

// px per animation frame at ~60fps → ≈ 40 px/sec
const AUTO_SCROLL_SPEED = 0.667;
// how long we pause auto-scroll after user interacts, in ms
const RESUME_DELAY = 2500;
// scroll distance per arrow click ≈ 1 card + gap
const CARD_STEP = 226;

const SampleReelCard = ({ sample, isPlaying, onPlay, onClose, testId }) => (
  <div className="w-[180px] md:w-[210px] shrink-0 select-none" data-testid={testId}>
    <div className="relative overflow-hidden rounded-[1rem] bg-neutral-100 aspect-[9/16]">
      {isPlaying ? (
        <>
          <iframe
            src={`https://player.vimeo.com/video/${sample.videoId}?autoplay=1&loop=1&badge=0&autopause=0&player_id=0&app_id=58479`}
            className="absolute inset-0 w-full h-full"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            title={sample.label}
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="Close video"
            className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-black/55 hover:bg-black/80 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
          >
            <X className="w-3 h-3" strokeWidth={2.25} aria-hidden="true" />
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={onPlay}
          className="absolute inset-0 block w-full h-full text-left focus:outline-none group/card"
          aria-label={`Play ${sample.label}`}
          draggable="false"
        >
          <img
            src={sample.thumb}
            alt={sample.label}
            loading="lazy"
            draggable="false"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover/card:scale-[1.04]"
          />
          <span className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/25" />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-11 h-11 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-[0_10px_30px_-10px_rgba(0,0,0,0.5)] transition-all duration-300 group-hover/card:scale-110 group-hover/card:bg-white">
              <Play className="w-4 h-4 text-neutral-950 fill-neutral-950 ml-0.5" aria-hidden="true" />
            </span>
          </span>
        </button>
      )}
    </div>
    <div className="mt-4 px-1">
      <div className="font-helvetica font-bold text-[0.55rem] md:text-[0.6rem] tracking-[0.32em] uppercase text-neutral-400 mb-1.5">
        {sample.num}
      </div>
      <div className="font-akira uppercase tracking-tight text-neutral-900 text-sm md:text-[0.95rem] leading-[1.1]">
        {sample.label}
      </div>
    </div>
  </div>
);

const ListingSampleCarousel = () => {
  const [playingIdx, setPlayingIdx] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const scrollRef = useRef(null);
  const isManuallyScrolling = useRef(false);
  const resumeTimeoutRef = useRef(null);

  // Duplicate list so translation can loop seamlessly
  const looped = [...listingSamples, ...listingSamples];

  const pauseAndResumeLater = () => {
    isManuallyScrolling.current = true;
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => {
      isManuallyScrolling.current = false;
    }, RESUME_DELAY);
  };

  // Unified rAF loop — auto-scrolls when free, and always handles wrap-around
  useEffect(() => {
    let rafId;
    const step = () => {
      const el = scrollRef.current;
      if (el) {
        const halfWidth = el.scrollWidth / 2;
        const shouldAutoScroll = playingIdx === null && !isHovered && !isManuallyScrolling.current;
        if (shouldAutoScroll && halfWidth > 0) {
          el.scrollLeft += AUTO_SCROLL_SPEED;
        }
        // Seamless wrap-around regardless of who caused the scroll
        if (halfWidth > 0) {
          if (el.scrollLeft >= halfWidth) el.scrollLeft -= halfWidth;
          else if (el.scrollLeft < 0) el.scrollLeft += halfWidth;
        }
      }
      rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [playingIdx, isHovered]);

  // Detect user interaction (wheel, touch, pointer drag) to pause auto-scroll
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = () => pauseAndResumeLater();
    const onTouch = () => pauseAndResumeLater();
    el.addEventListener("wheel", onWheel, { passive: true });
    el.addEventListener("touchstart", onTouch, { passive: true });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouch);
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    };
  }, []);

  // Mouse drag-to-scroll (desktop pointer drag)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let isDown = false;
    let startX = 0;
    let startScroll = 0;
    let dragMoved = false;

    const onDown = (e) => {
      // Only left button
      if (e.button !== 0) return;
      isDown = true;
      dragMoved = false;
      startX = e.pageX;
      startScroll = el.scrollLeft;
      pauseAndResumeLater();
    };
    const onMove = (e) => {
      if (!isDown) return;
      const dx = e.pageX - startX;
      if (Math.abs(dx) > 4) {
        dragMoved = true;
        el.classList.add("cursor-grabbing");
      }
      el.scrollLeft = startScroll - dx;
      pauseAndResumeLater();
    };
    const onUp = (e) => {
      isDown = false;
      el.classList.remove("cursor-grabbing");
      if (dragMoved) {
        // Prevent the mousedown-to-click from triggering play on cards
        e.preventDefault();
        e.stopPropagation();
      }
    };
    el.addEventListener("mousedown", onDown);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp, true);
    return () => {
      el.removeEventListener("mousedown", onDown);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp, true);
    };
  }, []);

  const scrollByCards = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * CARD_STEP, behavior: "smooth" });
    pauseAndResumeLater();
  };

  return (
    <div className="mt-14 md:mt-20" data-testid="listing-samples-carousel">
      <div className="text-center mb-8 md:mb-10">
        <p className="font-helvetica text-[0.65rem] md:text-xs tracking-[0.4em] uppercase text-neutral-500">
          See each style up close · Drag or use arrows
        </p>
      </div>

      <div
        className="relative -mx-6 md:-mx-12"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* edge fades */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-0 top-0 bottom-0 w-14 md:w-24 z-10"
          style={{ background: "linear-gradient(to right, #FAFAFA, rgba(250,250,250,0))" }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute right-0 top-0 bottom-0 w-14 md:w-24 z-10"
          style={{ background: "linear-gradient(to left, #FAFAFA, rgba(250,250,250,0))" }}
        />

        {/* Prev / Next arrow buttons — desktop */}
        <button
          type="button"
          data-testid="carousel-prev"
          onClick={() => scrollByCards(-1)}
          aria-label="Previous samples"
          className="hidden md:flex absolute left-3 lg:left-6 top-[42%] -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white items-center justify-center shadow-[0_8px_24px_-6px_rgba(0,0,0,0.25)] border border-neutral-200 hover:scale-110 hover:shadow-[0_12px_32px_-6px_rgba(0,0,0,0.3)] active:scale-95 transition-all"
        >
          <ChevronLeft className="w-5 h-5 text-neutral-900" strokeWidth={2} aria-hidden="true" />
        </button>
        <button
          type="button"
          data-testid="carousel-next"
          onClick={() => scrollByCards(1)}
          aria-label="Next samples"
          className="hidden md:flex absolute right-3 lg:right-6 top-[42%] -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-white items-center justify-center shadow-[0_8px_24px_-6px_rgba(0,0,0,0.25)] border border-neutral-200 hover:scale-110 hover:shadow-[0_12px_32px_-6px_rgba(0,0,0,0.3)] active:scale-95 transition-all"
        >
          <ChevronRight className="w-5 h-5 text-neutral-900" strokeWidth={2} aria-hidden="true" />
        </button>

        {/* Scroll track */}
        <div
          ref={scrollRef}
          className="overflow-x-auto scrollbar-hide cursor-grab"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <div className="flex gap-4 md:gap-5 py-2 px-6 md:px-12 w-max">
            {looped.map((sample, idx) => (
              <SampleReelCard
                key={idx}
                sample={sample}
                testId={`sample-card-${idx}`}
                isPlaying={playingIdx === idx}
                onPlay={() => setPlayingIdx(idx)}
                onClose={() => setPlayingIdx(null)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingSampleCarousel;
